import React, { useEffect, useState } from "react";
import {
  CustomCheckbox,
  CustomInputFieldSelect,
} from "../../../common/FormInputs";
import CaseService from "../../../../services/CaseService";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import { useSnackbar } from "notistack";
import useLoader from "../../../../hooks/useLoader";
import { getErrorMessage } from "../../../../helpers/functions";
import { Autocomplete } from "@material-ui/lab";
import { CircularProgress, Checkbox } from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";

const MultipleCustomSelectModal = ({ modal, setModal, id }) => {
  const [caseList, setCaseList] = useState([]);
  const [caseSelected, setCaseSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await CaseService.agentCases(id, modal.data.id);
        if (response?.caseDet) {
          const cases = response.caseDet;
          setCaseList(cases);
          setCaseSelected(cases.filter((item) => item.assigned));
        }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const allCases = caseList;
  const isAllSelected =
    allCases.length > 0 && caseSelected.length === allCases.length;

  const handleChange = (event, newValue) => {
    // Handle "Select All" logic
    if (newValue.some((item) => item.value === "selectall")) {
      setCaseSelected(isAllSelected ? [] : [...allCases]);
    } else {
      setCaseSelected(newValue);
    }
  };

  const onFormSubmit = async () => {
    try {
      setLoader({ state: true, message: "Assigning cases..." });
      const payload = {
        caseIds: caseSelected.map((item) => item.id),
      };
      await CaseService.assignCasesToAgents(modal.data.id, payload);
      enqueueSnackbar("Cases assigned successfully!", { variant: "success" });
      setModal({ state: false });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <Overlay>
      <ModalContainer>
        <Header>
          <Title>Assign Cases to Agent</Title>
          <CloseButton onClick={() => setModal({ state: false })}>
            <img
              src={require("../../../../assets/images/closeModal.svg")}
              alt="close"
            />
          </CloseButton>
        </Header>

        <Body>
          <Label>Select cases to assign</Label>

          <Autocomplete
            multiple
            options={allCases}
            disableCloseOnSelect
            value={caseSelected}
            onChange={handleChange}
            loading={loading}
            getOptionLabel={(option) => option.title || `Case ${option.id}`}
            noOptionsText="No cases available"
            renderOption={(option, { selected }) => (
              <OptionItem>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  style={{ color: "#00838c"}}
                />
                <OptionText>{option.title || `Case ${option.id}`}</OptionText>
              </OptionItem>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option.title || option.id}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <CustomInputFieldSelect
                {...params}
                variant="outlined"
                placeholder="Search cases..."
                className="input-white"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <SelectAllRow>
            <CustomCheckbox
              checked={isAllSelected}
              onChange={(e) =>
                setCaseSelected(e.target.checked ? [...allCases] : [])
              }
            />
            <SelectAllText>
              {isAllSelected
                ? "Deselect All"
                : `Select All (${allCases.length} cases)`}
            </SelectAllText>
          </SelectAllRow>

          {caseSelected.length > 0 && (
            <Summary>
              <strong>{caseSelected.length}</strong> case
              {caseSelected.length > 1 ? "s" : ""} selected
            </Summary>
          )}
        </Body>

        <Footer>
          <PrimaryOutlinedCloseCTAButton
            onClick={() => setModal({ state: false })}
          >
            Cancel
          </PrimaryOutlinedCloseCTAButton>
          <PrimaryCTAButton
            onClick={onFormSubmit}
            disabled={caseSelected.length === 0}
          >
            Assign Cases
          </PrimaryCTAButton>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default MultipleCustomSelectModal;

/* BEAUTIFUL & MODERN STYLED COMPONENTS */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  padding: 24px 32px 16px;
  color: white;
  position: relative;
  text-align: center;
`;

const Title = styled.h3`
  margin: 0;
  color: #293461;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 20px;
  font-weight: 800;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
  }
`;

const Body = styled.div`
  padding: 24px 32px;
  max-height: 70vh;
  overflow-y: auto;
`;

const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 12px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
`;

const OptionText = styled.span`
  margin-left: 8px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px 8px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background-color: #e9ecef;
  }
`;

const SelectAllText = styled.span`
  margin-left: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const Summary = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border-radius: 8px;
  text-align: center;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #1976d2;
  font-size: 15px;
`;

const Chip = styled.span`
  background-color: #00838c;
  color: white;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  margin: 2px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const Footer = styled.div`
  padding: 20px 32px;
  background-color: #f8f9fa;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  border-top: 1px solid #e0e0e0;

  button {
    min-width: 140px;
    padding: 12px 20px !important;
  }
`;
